import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StarRating from '@/components/base/StarRating';
import { comics, chapters, siteInfo } from '@/mocks/data';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const comicId = parseInt(id || '1', 10);
  const chapterId = searchParams.get('chapter') ? parseInt(searchParams.get('chapter') || '1', 10) : null;

  const comic = comics.find((c) => c.id === comicId);
  const comicChapters = chapters.filter((ch) => ch.comicId === comicId);
  const currentChapter = chapterId
    ? chapters.find((ch) => ch.id === chapterId && ch.comicId === comicId)
    : comicChapters[0] || null;

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
            <p className="text-foreground-500">漫画未找到</p>
            <a href="/" className="text-primary-500 hover:text-primary-600 text-sm mt-2 inline-block">返回首页</a>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const currentIndex = currentChapter ? comicChapters.findIndex((ch) => ch.id === currentChapter.id) : -1;
  const prevChapter = currentIndex > 0 ? comicChapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < comicChapters.length - 1 ? comicChapters[currentIndex + 1] : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: currentChapter ? `${comic.title} - ${currentChapter.title}` : comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: currentChapter?.updateTime,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '漫画分类', path: '/category' },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-[280px] shrink-0">
              <div className="sticky top-20">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-4">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 古古漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h1 className="font-heading text-xl text-foreground-950 mb-2">{comic.title}</h1>
                <StarRating rating={comic.rating} size="md" />
                <div className="mt-3 space-y-1.5 text-sm text-foreground-600">
                  <p><span className="text-foreground-400">作者：</span>{comic.author}</p>
                  <p><span className="text-foreground-400">状态：</span><span className="text-accent-500 font-medium">{comic.status}</span></p>
                  <p><span className="text-foreground-400">话数：</span>{comic.chapters}话</p>
                  <p><span className="text-foreground-400">人气：</span>{comic.views}</p>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              {currentChapter ? (
                <>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-1">{currentChapter.title}</h2>
                  <div className="flex items-center gap-3 text-xs text-foreground-500 mb-6">
                    <span>{currentChapter.updateTime}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {currentChapter.views}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={() => {
                        if (prevChapter) {
                          navigate(`/article/${comicId}?chapter=${prevChapter.id}`);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={!prevChapter}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                        prevChapter
                          ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                          : 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      }`}
                    >
                      <i className="ri-arrow-left-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      上一话
                    </button>
                    <span className="text-sm text-foreground-500">
                      {currentIndex + 1} / {comicChapters.length}
                    </span>
                    <button
                      onClick={() => {
                        if (nextChapter) {
                          navigate(`/article/${comicId}?chapter=${nextChapter.id}`);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={!nextChapter}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                        nextChapter
                          ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          : 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      }`}
                    >
                      下一话
                      <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    </button>
                  </div>

                  <div className="prose prose-sm max-w-none mb-8">
                    <p className="text-foreground-700 leading-relaxed">{currentChapter.content}</p>
                  </div>

                  {currentChapter.images && currentChapter.images.length > 0 && (
                    <div className="space-y-3 mb-8">
                      {currentChapter.images.map((img, idx) => (
                        <div key={idx} className="rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={img}
                            alt={`${comic.title} ${currentChapter.title} 第${idx + 1}页`}
                            title={`${comic.title} ${currentChapter.title} 漫画页`}
                            className="w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-6 border-t border-background-200/70">
                    <button
                      onClick={() => {
                        if (prevChapter) {
                          navigate(`/article/${comicId}?chapter=${prevChapter.id}`);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={!prevChapter}
                      className={`px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                        prevChapter
                          ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                          : 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      }`}
                    >
                      <i className="ri-arrow-left-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      上一话
                    </button>
                    <button
                      onClick={() => navigate(`/category`)}
                      className="px-5 py-2.5 bg-background-100 text-foreground-600 rounded-md text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                    >
                      <i className="ri-apps-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      目录
                    </button>
                    <button
                      onClick={() => {
                        if (nextChapter) {
                          navigate(`/article/${comicId}?chapter=${nextChapter.id}`);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={!nextChapter}
                      className={`px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                        nextChapter
                          ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          : 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      }`}
                    >
                      下一话
                      <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-16">
                  <i className="ri-book-open-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
                  <p className="text-foreground-500">暂无章节内容</p>
                </div>
              )}

              <div className="mt-10">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">章节列表</h3>
                <div className="border border-background-200/70 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                  {comicChapters.map((ch, index) => (
                    <div
                      key={ch.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-background-100/70 transition-colors ${
                        currentChapter?.id === ch.id ? 'bg-primary-50 border-l-2 border-l-primary-500' : ''
                      } ${index < comicChapters.length - 1 ? 'border-b border-background-200/50' : ''}`}
                      onClick={() => {
                        navigate(`/article/${comicId}?chapter=${ch.id}`);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                        currentChapter?.id === ch.id
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-500'
                      }`}>
                        {ch.number}
                      </span>
                      <span className={`text-sm truncate ${
                        currentChapter?.id === ch.id ? 'text-primary-600 font-medium' : 'text-foreground-700'
                      }`}>
                        {ch.title}
                      </span>
                      <span className="text-xs text-foreground-400 ml-auto shrink-0 whitespace-nowrap">{ch.updateTime}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}